import React, { useMemo, useState } from 'react';
import { Search, ShieldCheck, Hourglass, Beaker, GraduationCap, ArrowLeft } from 'lucide-react';
import { DIRECTIVES_73, DIRECTIVE_CATEGORIES, CATEGORY_KEYS } from '../lib/directives73';

/**
 * CodexPage — /codex
 * The HumanCodex — Federation Operational Manual.
 * Renders all 73 Sacred Directives with search + category filter chips.
 * Dark theme (#0a0a0f). University pillar branding.
 */

const TRUTH_LEVELS = [
  { label: 'FACT',       range: '95%+', color: '#32CD32', desc: 'Verified against Truth Gate. Cite source.' },
  { label: 'INFERENCE',  range: '70–94%', color: '#DAA520', desc: 'Reasoned from evidence. Show the chain.' },
  { label: 'ASSUMPTION', range: '40–69%', color: '#FF8C00', desc: 'Working hypothesis. Flag as unverified.' },
  { label: 'UNKNOWN',    range: '<40%',   color: '#FF6347', desc: 'Say so plainly. Never bluff.' },
];

export default function CodexPage() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DIRECTIVES_73.filter((d) => {
      const catMatch = activeCategory === 'all' || d.category === activeCategory;
      if (!catMatch) return false;
      if (!q) return true;
      return (
        d.id.toLowerCase().includes(q) ||
        d.title.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q)
      );
    });
  }, [query, activeCategory]);

  return (
    <div
      data-testid="codex-page"
      className="min-h-screen text-white"
      style={{ backgroundColor: '#0a0a0f' }}
    >
      {/* Header */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a
            href="/university"
            data-testid="codex-back-home"
            className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-emerald-300 hover:text-emerald-200 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            NextXus University
          </a>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-white/60">
            <GraduationCap className="w-3.5 h-3.5" style={{ color: '#32CD32' }} />
            <span style={{ color: '#32CD32' }}>University Pillar</span>
            <span className="text-white/30">·</span>
            <span>nextxus.help</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 space-y-10">
        {/* Title block */}
        <section data-testid="codex-title" className="space-y-3">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            The HumanCodex
          </h1>
          <p className="text-lg text-white/70">
            NextXus Federation Operational Manual
          </p>
          <p className="text-[11px] font-mono uppercase tracking-[0.24em]" style={{ color: '#32CD32' }}>
            Active Pillar: University — nextxus.help
          </p>
        </section>

        {/* 2226 Standard banner */}
        <section
          data-testid="codex-2226-banner"
          className="rounded-xl border p-6 flex items-start gap-4"
          style={{ borderColor: 'rgba(218, 165, 32, 0.4)', background: 'linear-gradient(135deg, rgba(218,165,32,0.08), rgba(50,205,50,0.04))' }}
        >
          <Hourglass className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: '#DAA520' }} />
          <div>
            <h2 className="text-lg font-semibold mb-1" style={{ color: '#DAA520' }}>
              ⏳ The 2226 Standard
            </h2>
            <p className="text-sm text-white/75 leading-relaxed">
              Every decision, every doctrine, every line of code must remain valid in the year <strong>2226</strong>.
              Two hundred years of knowledge preservation is the minimum horizon — for future minds we cannot yet imagine.
            </p>
          </div>
        </section>

        {/* Truth Gate */}
        <section
          data-testid="codex-truth-gate"
          className="rounded-xl border p-6"
          style={{ borderColor: 'rgba(50, 205, 50, 0.4)', background: 'rgba(50, 205, 50, 0.04)' }}
        >
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="w-6 h-6" style={{ color: '#32CD32' }} />
            <h2 className="text-lg font-semibold" style={{ color: '#32CD32' }}>
              🛡️ 95% Truth Gate (DIR-000)
            </h2>
          </div>
          <p className="text-sm text-white/75 leading-relaxed mb-4">
            All claims are gated against confidence thresholds. Below 95% confidence, content must be labeled honestly.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="truth-gate-levels">
            {TRUTH_LEVELS.map((lvl) => (
              <div
                key={lvl.label}
                data-testid={`truth-level-${lvl.label.toLowerCase()}`}
                className="rounded-lg border p-3"
                style={{ borderColor: `${lvl.color}66`, background: `${lvl.color}0d` }}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[11px] font-mono font-bold tracking-[0.18em]" style={{ color: lvl.color }}>
                    {lvl.label}
                  </span>
                  <span className="text-[10px] text-white/60 font-mono">{lvl.range}</span>
                </div>
                <p className="text-[11px] text-white/70 leading-snug">{lvl.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Forensic Output Format */}
        <section
          data-testid="codex-forensic-format"
          className="rounded-xl border p-6"
          style={{ borderColor: 'rgba(65, 105, 225, 0.4)', background: 'rgba(65, 105, 225, 0.04)' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Beaker className="w-6 h-6" style={{ color: '#4169E1' }} />
            <h2 className="text-lg font-semibold" style={{ color: '#4169E1' }}>
              🔬 Forensic Output Format
            </h2>
          </div>
          <p className="text-sm text-white/75 leading-relaxed mb-3">
            Every response — human or AI — labels its content by confidence tier so the reader can weigh it correctly.
          </p>
          <ul className="text-sm text-white/80 space-y-1 font-mono">
            <li><span style={{ color: '#32CD32' }}>FACT</span> — verified, sourced, ≥95% confidence.</li>
            <li><span style={{ color: '#DAA520' }}>INFERENCE</span> — reasoned from evidence, 70–94% confidence, chain shown.</li>
            <li><span style={{ color: '#FF8C00' }}>ASSUMPTION</span> — working hypothesis, 40–69% confidence, flagged.</li>
            <li><span style={{ color: '#FF6347' }}>UNKNOWN</span> — below 40% confidence. Say so plainly.</li>
          </ul>
        </section>

        {/* Search + filters */}
        <section data-testid="codex-controls" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search directives by ID, title, or keyword…"
                data-testid="codex-search-input"
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-black/60 border border-white/15 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-emerald-400/60 transition-colors"
              />
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50 self-center px-1" data-testid="codex-result-count">
              {filtered.length} / {DIRECTIVES_73.length} directives
            </div>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="codex-category-filters">
            <CategoryChip
              active={activeCategory === 'all'}
              onClick={() => setActiveCategory('all')}
              color="#ffffff"
              label="All"
              testId="filter-all"
            />
            {CATEGORY_KEYS.map((key) => (
              <CategoryChip
                key={key}
                active={activeCategory === key}
                onClick={() => setActiveCategory(key)}
                color={DIRECTIVE_CATEGORIES[key].color}
                label={DIRECTIVE_CATEGORIES[key].label}
                testId={`filter-${key}`}
              />
            ))}
          </div>
        </section>

        {/* Directive cards */}
        <section data-testid="codex-directives-list" className="grid gap-3 md:grid-cols-2">
          {filtered.map((d) => {
            const catColor = DIRECTIVE_CATEGORIES[d.category].color;
            const catLabel = DIRECTIVE_CATEGORIES[d.category].label;
            return (
              <article
                key={d.id}
                data-testid={`directive-${d.id}`}
                className="rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors border border-white/10 p-4 pl-5 relative overflow-hidden"
                style={{ borderLeft: `4px solid ${catColor}` }}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-[11px] font-mono font-bold tracking-[0.18em]" style={{ color: catColor }}>
                    {d.id}
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/40">
                    {catLabel}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-white leading-snug mb-1">
                  {d.title}
                </h3>
                <p className="text-[12px] text-white/65 leading-relaxed">
                  {d.description}
                </p>
              </article>
            );
          })}
          {filtered.length === 0 && (
            <div
              data-testid="codex-empty-state"
              className="col-span-full text-center py-12 text-white/50 text-sm font-mono"
            >
              No directives match your search.
            </div>
          )}
        </section>

        {/* Footer nudge */}
        <section
          data-testid="codex-footer-note"
          className="text-center text-[10px] font-mono uppercase tracking-[0.24em] text-white/40 pt-6 pb-2"
        >
          73 Sacred Directives · Cathedral Edition · Code Book Vol 1 · EchoCore64
        </section>
      </main>
    </div>
  );
}

function CategoryChip({ active, onClick, color, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-all"
      style={{
        borderColor: active ? color : 'rgba(255,255,255,0.15)',
        color: active ? '#0a0a0f' : color,
        backgroundColor: active ? color : 'transparent',
      }}
    >
      {label}
    </button>
  );
}
